<template>
  <div class="service-area-editor">
    <div class="service-area-editor__nationwide">
      <el-switch :value="area.nationwide" :disabled="disabled" @change="onNationwide"></el-switch>
      <span class="service-area-editor__label">{{ text.nationwideLabel }}</span>
      <span class="service-area-editor__hint">{{ text.nationwideHint }}</span>
    </div>
    <!-- 전국으로 저장하면 서버가 지역 목록을 버린다 — 폼 안에서만 돌아온다는 사실을 화면이 말한다 -->
    <div v-if="dropsAreasMessage" class="service-area-editor__warn">{{ dropsAreasMessage }}</div>

    <template v-if="!area.nationwide">
      <div class="service-area-editor__chips">
        <el-tag
          v-for="row in area.areas"
          :key="row.code"
          class="service-area-editor__chip"
          :closable="!disabled"
          disable-transitions
          @close="onRemove(row)"
          >{{ label(row) }}</el-tag
        >
        <span v-if="area.areas.length === 0" class="service-area-editor__hint">{{ text.emptyAreas }}</span>
      </div>
      <el-button size="mini" icon="el-icon-plus" :disabled="disabled" @click="openPicker">{{
        text.addByAddress
      }}</el-button>
    </template>

    <el-dialog
      :title="text.pickScopeTitle"
      :visible.sync="scopeVisible"
      width="420px"
      append-to-body
      :close-on-click-modal="false">
      <el-radio-group v-model="scopeCode" class="service-area-editor__scopes">
        <el-radio v-for="candidate in candidates" :key="candidate.code" :label="candidate.code">
          <span>{{ candidate.label }}</span>
          <span class="service-area-editor__hint">{{ candidate.name }}</span>
        </el-radio>
      </el-radio-group>
      <span slot="footer">
        <el-button size="small" @click="scopeVisible = false">취소</el-button>
        <el-button size="small" type="primary" :disabled="!scopeCode" @click="confirmScope">추가</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { pickAddress } from '@/utils/addressPicker'
import { latestGuard } from '@/utils/latestGuard'
import {
  ADD_AREA_RESULT,
  PLACE_AREA_TEXT,
  addArea,
  areaCandidatesOf,
  areaLabel,
  nationwideDropsAreasMessage,
  normalizeServiceArea,
  removeArea,
  setNationwide,
} from '@/utils/placeServiceArea'

/**
 * 플레이스 **운영지역 편집기** (B7-a 항목 6). `v-model` 은 `{nationwide, areas:[{code, name}]}` 이고
 * 저장은 하지 않는다 — 값만 만들어 부모(플레이스 폼)에게 올린다.
 *
 * **시군구 코드 표를 화면에 들이지 않는다**: 지역의 원천은 주소 위젯 하나(`utils/addressPicker`)이고,
 * 위젯이 준 `sigunguCode`·`sido`·`sigungu` 에서 후보를 만드는 계산은 순수 모듈(`utils/placeServiceArea`)이
 * 한다. 이 컴포넌트는 고르게 하고 그 답을 올릴 뿐이다.
 *
 * **고른 주소는 그 선택을 시작한 편집 세션의 것이다** — 근거와 무효화 규칙은 `PlaceBaseAddressField` 의
 * 같은 절과 하나다(둘 다 [latestGuard] 를 쓴다). 여기서는 범위 선택 대화상자가 사이에 끼므로 토큰이
 * 위젯 결과부터 「추가」까지 이어진다.
 */
export default {
  name: 'PlaceServiceAreaEditor',

  props: {
    /** `{nationwide, areas}` — `null` 이면 미설정으로 읽는다 */
    value: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    /** 편집 세션 — 부모가 폼을 채울 때마다 올린다. 바뀌면 앞선 주소 선택은 남의 것이 된다 */
    sessionKey: { type: Number, default: 0 },
  },

  data() {
    return {
      text: PLACE_AREA_TEXT,
      scopeVisible: false,
      scopeCode: '',
      scopeToken: 0,
      candidates: [],
      pickGuard: latestGuard(),
    }
  },

  watch: {
    sessionKey() {
      this.pickGuard.invalidate()
      this.scopeVisible = false
    },
  },

  computed: {
    /** 들어온 값이 무엇이든 편집기가 다루는 한 모양으로 — 「전국」과 「미설정」을 섞지 않는다 */
    area() {
      return normalizeServiceArea(this.value)
    },

    /** 전국을 켠 채 지역이 남아 있으면 저장에서 사라진다는 사실 — 없으면 `null`(할 말이 없다) */
    dropsAreasMessage() {
      return nationwideDropsAreasMessage(this.area)
    },
  },

  methods: {
    label(row) {
      return areaLabel(row)
    },

    onNationwide(on) {
      this.$emit('input', setNationwide(this.area, on))
    },

    onRemove(row) {
      this.$emit('input', removeArea(this.area, row.code))
    },

    async openPicker() {
      const token = this.pickGuard.begin()
      let picked = null
      try {
        picked = await pickAddress({ geocode: false })
      } catch (error) {
        if (this.pickGuard.isLatest(token)) this.$message({ type: 'error', message: error.message })

        return
      }
      // 기다리는 동안 다른 플레이스를 열었다 — 이 답은 지금 화면의 것이 아니다
      if (!picked || !this.pickGuard.isLatest(token)) return

      const candidates = areaCandidatesOf(picked)
      if (candidates.length === 0) {
        this.$message({ type: 'warning', message: this.text.invalid })

        return
      }
      // 후보가 하나뿐이면 물을 것이 없다 — 행정구가 있는 특정시에서만 「구만 / 시 전체」를 고르게 한다
      if (candidates.length === 1) {
        this.commit(candidates[0], token)

        return
      }

      this.candidates = candidates
      this.scopeCode = candidates[0].code
      this.scopeToken = token
      this.scopeVisible = true
    },

    confirmScope() {
      const candidate = this.candidates.find((row) => row.code === this.scopeCode)
      this.scopeVisible = false
      if (candidate) this.commit(candidate, this.scopeToken)
    },

    /** 중복·포함 판정은 순수 모듈이 하고, 화면은 그 답을 말한다 */
    commit(candidate, token) {
      if (!this.pickGuard.isLatest(token)) return
      const { serviceArea, result, message } = addArea(this.area, candidate)
      if (result === ADD_AREA_RESULT.ADDED || result === ADD_AREA_RESULT.ADDED_COVERING) {
        this.$emit('input', serviceArea)
      }
      if (message) {
        this.$message({ type: result === ADD_AREA_RESULT.ADDED_COVERING ? 'success' : 'warning', message })
      }
    },
  },
}
</script>

<style scoped>
.service-area-editor__nationwide {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.service-area-editor__label {
  font-weight: 600;
}

.service-area-editor__hint {
  color: #909399;
  font-size: 12px;
}

.service-area-editor__warn {
  margin-top: 6px;
  color: #e6a23c;
  font-size: 12px;
}

.service-area-editor__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0;
  min-height: 24px;
  align-items: center;
}

.service-area-editor__chip {
  margin-right: 0;
}

.service-area-editor__scopes {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
